import * as React from 'react';

import { InAppNotice } from './in-app-notice';
import {
  assessBrowserSupport,
  readBrowserProbe,
  UNSUPPORTED_BROWSER_BODY,
  UNSUPPORTED_BROWSER_DISMISS_LABEL,
  UNSUPPORTED_BROWSER_TITLE,
} from './supported-browsers';

/**
 * What a member on an unsupported browser is told (`PLT-008` AC-2).
 *
 * Before this, the answer was **nothing**. The support matrix did not exist, no code read one,
 * and a member on Safari 15 met a product that mostly worked and occasionally did not, with no
 * way to tell which. That is worse than either supporting the browser or refusing it, because
 * the member attributes the breakage to the product.
 *
 * ## It says something and gates nothing
 *
 * This is the **only** consumer of `assessBrowserSupport` in the application, and
 * `the-support-matrix.test.ts` fails if a second one appears. That is not tidiness: AC-2's
 * requirement is that reading, registration and checkout are never blocked, and the way they get
 * blocked is not by anybody deciding to block them — it is by a well-meaning `if (!supported)
 * return <UpgradePage />` somewhere upstream. There is one reader, it renders a sentence, and the
 * sentence sits above the screen rather than in place of it.
 *
 * ## Why it is not one of the three notification devices
 *
 * `MEM-NOT-002`'s three devices are how the product tells a member about *their own data* — a
 * match released, a request waiting — and every one of them routes to a surface inside the app
 * where the member can act. This routes nowhere, because the action it implies is "update your
 * browser", which is not something `/app` can offer. So it borrows the notice's shape, through
 * the same `InAppNotice` component that keeps every banner in this app looking like the same
 * banner, and it is deliberately absent from `IN_APP_NOTICES` — a notice registered there with a
 * surface it cannot honour would weaken the rule that every entry has one.
 *
 * ## The dismissal
 *
 * Remembered on the device, like the push offer's. A member who has read it and carried on has
 * answered; repeating it every session is how a notice trains people to skip notices. It is a
 * UI preference rather than member state — `MEM-042` AC-1's rule is that no member *data* lives
 * in local storage, and a "yes I have read this" is not data about anybody.
 */
const DISMISSED_KEY = 'h2hspark.browser-support-dismissed';

function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch {
    // Private browsing throws on `localStorage`. Treat it as "not yet dismissed" — showing the
    // notice once too often is a smaller cost than swallowing it entirely.
    return false;
  }
}

function rememberDismissed(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, 'true');
  } catch {
    // Nothing to do and nothing to tell the member: the notice goes away for this session, and
    // comes back next time. That is the correct degradation.
  }
}

export function UnsupportedBrowserNotice(): React.ReactElement | null {
  // Probed once per mount rather than per render. A browser does not grow a capability while a
  // member is looking at a screen, and `CSS.supports` is a real call.
  const [verdict] = React.useState(() => assessBrowserSupport(readBrowserProbe()));
  const [dismissed, setDismissed] = React.useState(true);

  // Read after mount rather than in the initial state, so a `localStorage` that throws does it
  // in an effect rather than during render.
  React.useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  if (verdict.supported) return null;
  if (dismissed) return null;

  return (
    <InAppNotice
      variant="warning"
      title={UNSUPPORTED_BROWSER_TITLE}
      body={UNSUPPORTED_BROWSER_BODY}
      onDismiss={() => {
        rememberDismissed();
        setDismissed(true);
      }}
      dismissLabel={UNSUPPORTED_BROWSER_DISMISS_LABEL}
      className="mb-4"
    />
  );
}
